// Tenant settings (PLAN-P1/P2 onboarding leftover) — rename the tenant (self-serve provisioning
// auto-names it from the email domain) + set the default language. Edit gated on tenant_settings.update.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { useI18n } from "../lib/i18n";
import { AUTH_BASE } from "../lib/env";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const errText = (e: unknown) => e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message;

export function SettingsRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["tenant-settings"], queryFn: () => api.getTenantSettings() });
  const s = (data ?? null) as Record<string, unknown> | null;
  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={t("settings.title")} />
      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{errText(error)}</p>}
      {s && <SettingsForm s={s} onSaved={() => qc.invalidateQueries({ queryKey: ["tenant-settings"] })} />}
      <Can perm="super_tenant.impersonate_tenant"><ImpersonatePanel /></Can>
    </section>
  );
}

// F5 — super-tenant operator: start a read-only act-as session for another tenant (audited).
function ImpersonatePanel() {
  const { t } = useI18n();
  const [tenantId, setTenantId] = useState("");
  const [reason, setReason] = useState("");
  const start = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${AUTH_BASE}/auth/impersonate`, {
        method: "POST", credentials: "include", headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: tenantId.trim(), reason: reason.trim() || undefined }),
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text().catch(() => "")}`);
      return res.json();
    },
    onSuccess: () => { window.location.href = "/"; }, // reboot the session (now scoped to the target, read-only)
  });
  return (
    <Card className="max-w-xl">
      <CardHeader><CardTitle>{t("imp.start")}</CardTitle></CardHeader>
      <CardContent>
        <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
          <div className="grid gap-1.5">
            <Label htmlFor="imp-tenant">{t("imp.tenantId")}</Label>
            <Input id="imp-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="uuid" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="imp-reason">{t("imp.reason")}</Label>
            <Input id="imp-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          {start.isError && <p className="text-sm text-destructive">{(start.error as Error).message}</p>}
          <div>
            <Button type="submit" disabled={start.isPending || tenantId.trim().length < 10}>{start.isPending ? "…" : t("imp.go")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function SettingsForm({ s, onSaved }: { s: Record<string, unknown>; onSaved: () => void }) {
  const { t } = useI18n();
  const [name, setName] = useState(String(s.name ?? ""));
  const [lang, setLang] = useState(String(s.defaultLanguage ?? "nl"));
  const save = useMutation({ mutationFn: () => api.updateTenantSettings({ name: name.trim(), defaultLanguage: lang }), onSuccess: onSaved });
  return (
    <Card className="max-w-xl">
      <CardContent>
        <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <p className="text-sm text-muted-foreground">{t("settings.class")}: <code>{String(s.tenantClass ?? "—")}</code></p>
          <div className="grid gap-1.5">
            <Label htmlFor="settings-name">{t("settings.name")}</Label>
            <Input id="settings-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="settings-lang">{t("settings.language")}</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger id="settings-lang" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nl">Nederlands (nl)</SelectItem>
                <SelectItem value="en">English (en)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {save.isError && <p className="text-sm text-destructive">{errText(save.error)}</p>}
          {save.isSuccess && <p className="text-sm text-emerald-600">{t("settings.saved")}</p>}
          <Can perm="tenant_settings.update" fallback={<p className="text-sm text-muted-foreground">{t("settings.readonly")}</p>}>
            <div>
              <Button type="submit" disabled={save.isPending || !name.trim()}>{save.isPending ? t("common.loading") : t("common.save")}</Button>
            </div>
          </Can>
        </form>
      </CardContent>
    </Card>
  );
}
